import {uniqBy} from 'lodash-es';

import {
  getTagboards,
  getMultipleTagboards,
  getTags,
} from '../../../services/tagboards';

const state = {
  tagboards: [],
  tagboardTags: [],
  lookupTable: {},

  // Track which tagboards are being loaded by ID
  isLoadingMap: {},

  // Track which tagboards are confirmed missing (deleted) by ID
  notFoundMap: {},

  isLoading: false,
  page: 1,
  limit: 20,
  total: 0,
  tags: [],
  search: '',
};

const actions = {
  async getTagboards(context) {
    const { tags, page, limit, search } = context.state;

    context.commit('SET_BOARDS_FIELD', { key: 'isLoading', value: true });

    const resp = await getTagboards({
      page,
      limit,
      tags: tags.join(','),
      search,
    });

    const { tagboards, total } = resp;

    context.commit('SET_BOARDS_FIELD', { key: 'isLoading', value: false });
    context.commit('SET_BOARDS', tagboards);
    context.commit('ADD_BOARDS_TO_LOOKUP_TABLE', tagboards);
    context.commit('SET_BOARDS_FIELD', { key: 'total', value: total });
  },

  async getTagboardTags({ commit }) {
    const { tags } = await getTags();
    const sortedTags = [...tags].sort((a, b) => a.localeCompare(b));

    commit('SET_BOARDS_FIELD', { key: 'tagboardTags', value: sortedTags });
  },

  async getTagboard(context, { tagboardId }) {
    const resp = await getMultipleTagboards([tagboardId]).catch((err) => {
      console.error('Failed to fetch tagboard by ID:', tagboardId, err);
    });

    if (resp?.tagboards?.length) {
      context.commit('ADD_BOARDS_TO_LOOKUP_TABLE', resp.tagboards);
    }
  },

  /**
   * Loop over production timeline and grab any tagboards in themes.
   * Then fetch only those tagboards from the API to fill the lookup table.
   */
  async getTimelineTagboards(context) {
    const { production } = context.rootState.production;
    const { timeline } = production;

    const tagboardIds = timeline.reduce((memo, post) => {
      if (post?.posts?.length) {
        const [timelinePost] = post.posts;

        if (timelinePost?.type === 'tagboard') {
          return memo.add(timelinePost._id);
        }
      }

      return memo;
    }, new Set());

    // Ignore tagboards already in the lookup table or already confirmed missing.
    const idsToFetch = [...tagboardIds].filter((id) => (
      !context.state.lookupTable[id]
      && !context.state.notFoundMap[id]
    ));

    if (!idsToFetch.length) {
      return;
    }

    context.commit('SET_BOARDS_LOADING', { ids: idsToFetch, loading: true });

    try {
      const resp = await getMultipleTagboards(idsToFetch);

      if (resp?.tagboards?.length) {
        context.commit('ADD_BOARDS_TO_LOOKUP_TABLE', resp.tagboards);
      }

      // Any tagboard requested but missing from the response is presumed deleted.
      const returnedIds = new Set((resp?.tagboards || []).map((board) => board._id));
      const notFoundIds = idsToFetch.filter((id) => !returnedIds.has(id));
      if (notFoundIds.length) {
        context.commit('SET_BOARDS_NOT_FOUND', { ids: notFoundIds, notFound: true });
      }
    } catch (err) {
      console.error('Failed to fetch tagboards by ID(s)', idsToFetch, err);
    } finally {
      context.commit('SET_BOARDS_LOADING', { ids: idsToFetch, loading: false });
    }
  },

  /**
   * Clear list of boards
   */
  resetTagboards(context) {
    context.commit('RESET_BOARDS');
  },

  /**
   * Increment page and fetch next page of data
   */
  incrementBoardsPage(context) {
    context.commit('SET_BOARDS_FIELD', { key: 'page', value: context.state.page + 1 });
    context.dispatch('getTagboards');
  },

  changeSocialBoardSearch({ commit, dispatch }, search) {
    commit('SET_BOARDS_FIELD', { key: 'search', value: search });
    commit('RESET_BOARDS');
    dispatch('getTagboards');
  },

  changeSocialBoardTags({ commit, dispatch }, tags) {
    commit('SET_BOARDS_FIELD', { key: 'tags', value: tags });
    commit('RESET_BOARDS');
    dispatch('getTagboards');
  },

  /**
   * Clear all search tags and fetch boards
   */
  resetSocialBoardSearch(context) {
    context.commit('CLEAR_BOARDS_TAGS');
    context.commit('SET_BOARDS_FIELD', { key: 'search', value: '' });
    context.commit('RESET_BOARDS');
    context.dispatch('getTagboards');
  },

  setTagboards(context, payload) {
    context.commit('SET_BOARDS', payload);
  },
};

const mutations = {
  /* eslint-disable no-shadow, no-param-reassign */

  SET_BOARDS(state, boards) {
    state.tagboards = uniqBy(
      state.tagboards.concat(boards),
      '_id',
    );
  },

  ADD_BOARDS_TO_LOOKUP_TABLE(state, boards) {
    if (!boards) { return; }

    state.lookupTable = {
      ...state.lookupTable,
      ...(boards.reduce((memo, board) => ({
        ...memo,
        [board._id]: {
          ...board,
        },
      }), {})),
    };
  },

  RESET_BOARDS(state) {
    state.tagboards = [];
    state.page = 1;
  },

  /**
  * Generic mutator for setting any field to the provided value
  */
  SET_BOARDS_FIELD(state, { key, value }) {
    state[key] = value;
  },

  ADD_BOARDS_TAG(state, tag) {
    state.tags = state.tags.concat([tag]);
  },

  REMOVE_BOARDS_TAG(state, tag) {
    state.tags = state.tags.filter((t) => t !== tag);
  },

  CLEAR_BOARDS_TAGS(state) {
    state.tags = [];
  },

  /**
   * Set loading flags for specific tagboards by ID,
   * so the playlist can show a skeleton while they load.
   */
  SET_BOARDS_LOADING(state, { ids, loading }) {
    state.isLoadingMap = {
      ...state.isLoadingMap,
      ...ids.reduce((memo, id) => ({
        ...memo,
        [id]: loading,
      }), {}),
    };
  },

  SET_BOARDS_NOT_FOUND(state, { ids, notFound }) {
    state.notFoundMap = {
      ...state.notFoundMap,
      ...ids.reduce((memo, id) => ({
        ...memo,
        [id]: notFound,
      }), {}),
    };
  },
};

const getters = {
  hasMoreBoards(state) {
    return state.total > (state.page * state.limit);
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
